import { TextInput, Text, Icon } from "@ucl/react-library";
import { Control, FieldErrors, useController, useFormContext, ControllerRenderProps, FieldPath } from "react-hook-form";
import { TFunction } from "i18next";
import { autoTabHandler } from "src/utils/auto-tab";
import { parseDOBErrorStates } from "src/utils/dob-error-parser";
import React from "react";
import { AuthorisationFormValues } from "src/types/form-data";

interface DOBInputProps {
  readonly t: TFunction;
  readonly control: Control<AuthorisationFormValues>;
  readonly errors: FieldErrors<AuthorisationFormValues>;
}

export function DOBInput({ t, control, errors }: DOBInputProps) {
  const { trigger } = useFormContext();
  const { field: dayField } = useController({ name: "dobDay", control });
  const { field: monthField } = useController({ name: "dobMonth", control });
  const { field: yearField } = useController({ name: "dobYear", control });

  const dobErrorMessage = errors.dobCombined?.message;
  const errorStates = parseDOBErrorStates(dobErrorMessage);

  const handleDayKeyUp = autoTabHandler("dobMonth", 2);
  const handleMonthKeyUp = autoTabHandler("dobYear", 2);

  const isMovingWithinDOBFields = (relatedTarget: EventTarget | null): boolean => {
    if (!relatedTarget) return false;
    const target = relatedTarget as HTMLInputElement;
    return ["dobDay", "dobMonth", "dobYear"].includes(target.name);
  };

  const padSingleDigit = (value: string): string => {
    return value.length === 1 ? `0${value}` : value;
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    field: ControllerRenderProps<AuthorisationFormValues, FieldPath<AuthorisationFormValues>>
  ) => {
    if (e.target.name === "dobDay" || e.target.name === "dobMonth") {
      field.onChange(padSingleDigit(e.target.value));
    }
    field.onBlur();
    if (!isMovingWithinDOBFields(e.relatedTarget)) {
      trigger("dobCombined");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row gap-2">
        <Text.P size="large" data-testid="subheading" style={{color: "rgb(0, 20, 46)"}}>
          {t("personalDetails.labels.dobCombined")}
        </Text.P>
      </div>
      <div className="flex gap-3 dob-inputs">
        <div className="w-form-field-xxxs">
          <TextInput
            data-testid="dobDay"
            autoComplete="bday-day"
            inputMode="numeric"
            labelProp={{
              label: t("personalDetails.labels.dobDay"),
            }}
            placeholder=""
            onKeyUp={handleDayKeyUp}
            inputState={errorStates.day ? "error" : "none"}
            type="number"
            {...dayField}
            onBlur={(e) => handleBlur(e, dayField)}
          />
        </div>
        <div className="w-form-field-xxxs">
          <TextInput
            data-testid="dobMonth"
            autoComplete="bday-month"
            inputMode="numeric"
            labelProp={{
              label: t("personalDetails.labels.dobMonth"),
            }}
            type="number"
            {...monthField}
            placeholder=""
            onKeyUp={handleMonthKeyUp}
            onBlur={(e) => handleBlur(e, monthField)}
            inputState={errorStates.month ? "error" : "none"}
          />
        </div>
        <div className="w-form-field-xxs">
          <TextInput
            data-testid="dobYear"
            autoComplete="bday-year"
            inputMode="numeric"
            labelProp={{
              label: t("personalDetails.labels.dobYear"),
            }}
            placeholder=""
            type="number"
            {...yearField}
            onBlur={(e) => handleBlur(e, yearField)}
            inputState={errorStates.year ? "error" : "none"}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <Text.P className="!text-helper-text text-sm">{t("personalDetails.dobHelperText")}</Text.P>
        {dobErrorMessage && (
          <div className="flex flex-row gap-2" data-testid="dobError">
            <Icon icon="warningFilled" size="xxs" className="text-error" />
            <Text.P size="small" className="!text-error">
              {dobErrorMessage}
            </Text.P>
          </div>
        )}
      </div>
    </div>
  );
}
